import { useTranslation } from "react-i18next";

import {
  getAdditionalCostTitle,
  getProjectAdditionalCosts,
} from "@/components/Project/ProjectCostsUtils";
import { useReferentialProjectCostTypes } from "@/entities/referential/referential-project-cost-types";

const NUMBER_OF_ADDITIONAL_COSTS_SELECTED = 2;

const COLORS = {
  interventionsCost: "#ff912b",
  additionalCosts: {
    highlighted: ["#ce5847", "#90cc7c"],
    others: "#d9d9d9",
  },
};

export const useProjectsCostsCardData = (project) => {
  const { t } = useTranslation();
  const { costTypes } = useReferentialProjectCostTypes();

  const interventionsCost = project?.interventions_cost;
  const interventionsRevisedCost = project?.revised_interventions_cost;

  const additionalCosts = getProjectAdditionalCosts(
    project,
    NUMBER_OF_ADDITIONAL_COSTS_SELECTED
  );

  const highlightedLength = additionalCosts.highlighted?.length || 0;
  const hasHighlightedCosts = highlightedLength > 0;

  const othersTotalCost = additionalCosts.othersTotalCost ?? 0;
  const othersLength = additionalCosts.others?.length || 0;
  const hasOthersCosts = othersLength > 0;

  const showOthers =
    othersTotalCost > 0 && (!hasHighlightedCosts || hasOthersCosts);

  const othersTitleKey = hasHighlightedCosts
    ? "othersAdditionalCosts"
    : "additionalCosts";

  return {
    interventionsCost: interventionsCost
      ? {
          name: t("project:costsCard:interventionsCost"),
          value: interventionsCost,
          color: COLORS.interventionsCost,
        }
      : undefined,
    interventionsRevisedCost: interventionsRevisedCost
      ? {
          name: t("project:costsCard:revisedInterventionsCost"),
          value: interventionsRevisedCost,
          color: COLORS.interventionsCost,
        }
      : undefined,
    additionalCostsHighlighted: (additionalCosts.highlighted ?? []).map(
      (cost, index) => ({
        id: cost.id,
        name: getAdditionalCostTitle({ cost, costTypes }),
        value: cost.estimated_cost ?? 0,
        color: COLORS.additionalCosts.highlighted[index],
      })
    ),
    additionalCostsOthers: showOthers
      ? {
          name: t(`project:costsCard:${othersTitleKey}`),
          value: othersTotalCost,
          color: COLORS.additionalCosts.others,
          subCosts: additionalCosts.others?.map((cost) => ({
            id: cost.id,
            name: getAdditionalCostTitle({ cost, costTypes }),
            value: cost.estimated_cost ?? 0,
          })),
        }
      : undefined,
    estimatedTotalCost: {
      name: t("project:costsCard:totalCost"),
      value: project?.estimated_total_cost ?? 0,
    },
  };
};
